import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import CustomEditor from "../common/Editor";
import { FolderOpen } from "lucide-react";
import FileUploadModal from "../exam-builder/FileUploadModal"; // مسیرت رو چک کن
import MediaPickerModal from "./MediaPickerModal";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  audio_url: "",
  allowPause: false,
  maxPlays: 1,
  mustListen: true,
  seg_start: "",
  seg_end: "",
  points: 0,
  status: "published",
  tags: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";
  const body = item?.body || {};
  const audio = body.audio || {};
  const segment = body.segment || {};

  return {
    stem_fa,
    stem_en,
    audio_url: audio.url || "",
    allowPause: !!audio.allowPause,
    maxPlays:
      typeof audio.maxPlays === "number" && !Number.isNaN(audio.maxPlays)
        ? audio.maxPlays
        : 1,
    mustListen: typeof body.mustListen === "boolean" ? body.mustListen : true,
    seg_start: typeof segment.startSec === "number" ? segment.startSec : "",
    seg_end: typeof segment.endSec === "number" ? segment.endSec : "",
    points:
      typeof item.points === "number" && !Number.isNaN(item.points)
        ? item.points
        : 0,
    status: item.status || "published",
    tags: Array.isArray(item.tags) ? item.tags.join(",") : "",
  };
};

export default function AudioGateModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());
  const [fileModalOpen, setFileModalOpen] = useState(false);

  React.useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.audio_url.trim()) {
      toast.error(t("audio_gate.toasts.audio_required", "آدرس صوت الزامی است"));
      return;
    }

    const seg =
      form.seg_start !== "" || form.seg_end !== ""
        ? {
            startSec:
              form.seg_start !== "" ? Number(form.seg_start) : undefined,
            endSec: form.seg_end !== "" ? Number(form.seg_end) : undefined,
          }
        : undefined;

    const payload = {
      kind: "audio_gate",
      stem: { fa: form.stem_fa || undefined, en: form.stem_en || undefined },
      body: {
        audio: {
          url: form.audio_url,
          allowPause: !!form.allowPause,
          maxPlays: Number(form.maxPlays) || 1,
        },
        segment: seg,
        mustListen: !!form.mustListen,
      },
      points: Number(form.points) || 0,
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");
      toast.success(
        isEditMode
          ? t("audio_gate.toasts.updated", "به‌روزرسانی شد")
          : t("audio_gate.toasts.created", "ساخته شد")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      {/* Stem */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("audio_gate.stem_fa", "راهنمای گوش‌دادن (فارسی)")}
          </label>
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) =>
              setForm((f) => ({ ...f, stem_fa: content }))
            }
            placeholder={t(
              "audio_gate.stem_fa_ph",
              "مثلاً: لطفاً به این بخش با دقت گوش دهید."
            )}
            directionality={isRtl ? "rtl" : "ltr"}
            height={120}
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("audio_gate.stem_en", "Listening prompt (EN)")}
          </label>
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) =>
              setForm((f) => ({ ...f, stem_en: content }))
            }
            placeholder={t("audio_gate.stem_en_ph", "Please listen carefully.")}
            directionality="ltr"
            height={120}
          />
        </div>
      </div>

      {/* Audio config */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">
            {t("audio_gate.audio_url", "آدرس فایل صوتی")}
          </label>

          <div className="flex gap-2">
            <input
              name="audio_url"
              className="flex-1 border rounded-lg px-3 py-2"
              placeholder="https://…/audio.mp3"
              value={form.audio_url}
              onChange={onChange}
            />

            <button
              type="button"
              onClick={() => setFileModalOpen(true)}
              className="px-3 py-2 rounded-lg border bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2 whitespace-nowrap"
              title={t("common.browse", "Browse")}
            >
              <FolderOpen className="w-4 h-4" />
              {t("common.browse", "Browse")}
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("audio_gate.max_plays", "حداکثر پخش")}
          </label>
          <input
            type="number"
            name="maxPlays"
            min={1}
            className="w-full border rounded-lg px-3 py-2"
            value={form.maxPlays}
            onChange={onChange}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="flex items-center gap-2">
          <input
            id="allowPause"
            type="checkbox"
            name="allowPause"
            checked={form.allowPause}
            onChange={onChange}
          />
          <label htmlFor="allowPause" className="text-sm">
            {t("audio_gate.allow_pause", "اجازه مکث")}
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="mustListen"
            type="checkbox"
            name="mustListen"
            checked={form.mustListen}
            onChange={onChange}
          />
          <label htmlFor="mustListen" className="text-sm">
            {t("audio_gate.must_listen", "باید کامل گوش شود")}
          </label>
        </div>
      </div>

      {/* Segment (optional) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("audio_gate.segment_start", "شروع ثانیه (اختیاری)")}
          </label>
          <input
            type="number"
            name="seg_start"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.seg_start}
            onChange={onChange}
            placeholder="مثلاً 0"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("audio_gate.segment_end", "پایان ثانیه (اختیاری)")}
          </label>
          <input
            type="number"
            name="seg_end"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.seg_end}
            onChange={onChange}
            placeholder="مثلاً 42"
          />
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("audio_gate.points", "امتیاز")}
          </label>
          <input
            type="number"
            name="points"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={onChange}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("audio_gate.status", "وضعیت")}
          </label>
          <select
            name="status"
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={onChange}
          >
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("audio_gate.tags", "تگ‌ها")}
          </label>
          <input
            name="tags"
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            onChange={onChange}
          />
        </div>
      </div>

      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>

      <MediaPickerModal
        isOpen={fileModalOpen}
        onClose={() => setFileModalOpen(false)}
        token={token}
      />
    </form>
  );
}
